import { useMemo } from 'react'
import { useMutation } from '@tanstack/react-query'
import { addToast } from '@mobi/component-library/Feedback/Toast'
import { AppRoutes } from '@core/AppRoutes'
import { queryClient } from '@core/Data/ReactQuery/queryClient'
import { queryKeys } from '@core/Data/ReactQuery/constants'
import { useObservableImmutable } from '@core/Utils/hooks'
import { state$ as useAccountState$ } from '@core/State/UserAccount/userAccountDriver'
import { showLogin } from '@core/Areas/Login/helpers'
import { postAddRunnerToBlackbook } from '@core/Areas/Blackbook/helpers/api'
import {
  trackBlackbookAddRunner,
  trackBlackbookGoToBlackbook,
  trackBlackbookUnableToAdd,
} from '@core/Areas/Blackbook/analytics'
import { useBlackbookRunners } from '@core/Areas/Blackbook/Hooks/useBlackbookRunners'
import { getMatchedRunnerInBlackbook } from '@core/Areas/Blackbook/helpers/getMatchedRunnerInBlackbook'
import type { BlackbookControlProps } from '@core/Areas/Blackbook/types'
import { triggerHapticFeedback } from '@core/Utils/hapticFeedback/hapticFeedback'
import { appHistory } from '@core/Utils/routing/history'

export const useAddToBlackbook = ({ starterName, starterType, source }: BlackbookControlProps) => {
  const { isLoggedIn, accountNumber } = useObservableImmutable(useAccountState$, [
    'isLoggedIn',
    'accountNumber',
  ])
  const { data: blackbookRunners, isLoading: isLoadingRunners } = useBlackbookRunners()

  const matchedRunnerInBlackbook = useMemo(() => {
    return isLoggedIn
      ? getMatchedRunnerInBlackbook(blackbookRunners, starterName, starterType)
      : undefined
  }, [blackbookRunners, isLoggedIn, starterName, starterType])

  const { mutate, isPending } = useMutation<PostRes, void, PostArgs>({
    mutationFn: ({ starterName, starterType }) =>
      postAddRunnerToBlackbook({ starterName, starterType }),
    onSuccess: res => {
      if (!res.success)
        return handleUnableToAdd({
          source,
          starterName,
          starterType,
          errorMessage: res.errorMessage,
        })

      queryClient.invalidateQueries({
        queryKey: queryKeys.blackbookRunners(accountNumber),
        refetchActive: true,
      })
      queryClient.invalidateQueries({ queryKey: [queryKeys.blackbookDetails], refetchActive: true })

      if (source) trackBlackbookAddRunner({ source, name: starterName, code: starterType })
      triggerHapticFeedback('impact-light')
    },
    onError: () => {
      handleUnableToAdd({ source, starterName, starterType })
    },
  })

  const handleAddToBlackbook = () =>
    isLoggedIn
      ? mutate({ starterName, starterType })
      : showLogin({ onLoginSuccessCallback: () => mutate({ starterName, starterType }) })

  const handleNavigateToBlackbook = (view?: 'runners' | 'jockeys') => {
    const queryParams = new URLSearchParams()
    if (matchedRunnerInBlackbook?.Id !== undefined) {
      queryParams.append('incoming', matchedRunnerInBlackbook?.Id?.toString())
    }
    if (view !== undefined) {
      queryParams.append('view', view)
    }
    appHistory.push(`${AppRoutes.Blackbook}?${queryParams}`)
    source && trackBlackbookGoToBlackbook({ source, name: starterName, code: starterType })
  }

  return {
    isPending,
    isLoadingRunners,
    matchedRunnerInBlackbook,
    clickHandler: (tab?: 'runners' | 'jockeys') => {
      if (matchedRunnerInBlackbook) {
        handleNavigateToBlackbook(tab)
      } else {
        handleAddToBlackbook()
      }
    },
  }
}

// =============
// Local Helpers
// =============

function handleUnableToAdd({
  source,
  starterName,
  starterType,
  errorMessage,
}: BlackbookControlProps & { errorMessage?: string }) {
  addToast({
    id: 'blackbook-control-add',
    title: 'Unable to Add to Blackbook',
    icon: 'error',
  })
  const reason: Parameters<typeof trackBlackbookUnableToAdd>[0]['reason'] =
    !errorMessage || typeof errorMessage !== 'string'
      ? 'other'
      : errorMessage.toLowerCase().includes('unable to resolve starter')
        ? 'not-matched'
        : errorMessage.toLowerCase().includes('maximum number')
          ? 'limit-exceeded'
          : 'other'
  trackBlackbookUnableToAdd({ source, name: starterName, code: starterType, reason })
}

// =====
// Types
// =====

type PostArgs = Parameters<typeof postAddRunnerToBlackbook>[0]
type PostRes = Awaited<ReturnType<typeof postAddRunnerToBlackbook>>
